import React, { useEffect, useState } from "react";
import Button from "../../Button/Button";
import { TextInput } from "../..";
import { addOfficeRequestFinalDocuments, requestFilesType } from "../../../redux/slices/OfficeRequestSlice";
import { DragDropContext, Droppable, Draggable, DraggingStyle, NotDraggingStyle, DropResult } from "react-beautiful-dnd";
import { mergePDF } from "../../../utils";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { DsFile } from "../../../models/ds-file.model";
import { FileTypes } from "../../../models";
import { uploadFileForMerge, uploadMultipleFileForMerge } from "../../../redux/slices/FileSlice";
import { colors } from "../../../utils/constants";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Typography, Paper, List, ListItem, IconButton } from "@mui/material";

interface FinalDocumentsType {
  files: requestFilesType[];
  requestId: string;
  officeId: string;
  closeForm: (val?: boolean) => void;
}
let clickedCloseForm = false;

const FinalDocuments = ({ files, officeId, requestId, closeForm }: FinalDocumentsType) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorInitiated, setErrorInitiated] = useState(false);
  const [fileNameValidation, setfileNameValidation] = useState(true);
  const [fileName, setFileName] = useState("Final Document");
  const [mergingPending, setMergingPending] = useState(false);
  const [fileError, setFileError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [items, setItems] = useState<FinalDocumentsType["files"]>(files);
  const dispatch = useDispatch<AppDispatch>();
  const validateForm = () => {
    if (fileNameValidation) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    clickedCloseForm = false;
  }, []);

  useEffect(() => {
    if (fileName.length) handleChangeFileName(fileName);
  }, [fileName]);

  const saveFinalFiles = () => {
    mergeFiles();
  };

  const reorder = (list: FinalDocumentsType["files"], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const tempItems = reorder(items, result.source.index, result.destination.index);
    setItems(tempItems);
  };
  const grid = 8;
  const getItemStyle = (isDragging: boolean, draggableStyle?: DraggingStyle | NotDraggingStyle) =>
    ({
      ...draggableStyle,
    } as unknown as React.CSSProperties);
  const getListStyle = () => ({
    padding: grid,
    width: "auto",
  });

  const mergeFiles = async () => {
    setMergingPending(true);
    const filePromises = await Promise.all(
      items.map(async f => {
        if (!f.url) {
          throw new Error(`File URL is missing for ${f.originalFilename}`);
        }
        const response = await fetch(f.url);
        const blob = await response.blob();
        return new File([blob], f.originalFilename, { type: f.contentType || "application/pdf" });
      })
    );

    if (!clickedCloseForm) {
      dispatch(
        uploadMultipleFileForMerge({
          files: filePromises,
          fileName: fileName,
        })
      )
        .then(action => {
          setMergingPending(false);
          if (officeId && requestId && action?.payload && (action.payload as DsFile)?.fileId) {
            dispatch(addOfficeRequestFinalDocuments({ officeId: officeId, requestId: requestId, fileIds: [(action.payload as DsFile).fileId] }));
            closeForm();
          }
        })
        .catch(err => {
          setFileError(`Failed to save final document: ${err.message}`);
          setMergingPending(false);
        });
    }
  };

  const handleChangeFileName = (e: string) => {
    // Remove dots from the input
    const filteredValue = e.replace(/\./g, "");
    setFileName(filteredValue);
    if (filteredValue.trim().length > 0) {
      setfileNameValidation(false);
    } else {
      setfileNameValidation(true);
    }
  };

  return (
    <>
      <p className="text-base px-8 mt-2 py-3 font-medium">You can create multiple final docs. Name your file below.</p>

      <div className="px-8 ">
        <TextInput
          onChangeFunc={e => handleChangeFileName(e.currentTarget.value)}
          value={fileName}
          autoFocus
          label="Document Name"
          errorMsg={"Please enter file name"}
          isError={errorInitiated && fileNameValidation}
          isRequired
          placeholder="Add file name"
          onBlur={e => handleChangeFileName(e.currentTarget.value.trim())}
          isDarkBg={true}
        />
        <p className="py-4">Reorder the files by dragging the handles. Files will be merged in the order they appear below...</p>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={getListStyle()}>
                {items.map((item, index: number) => (
                  <Draggable key={item.requestFileId} draggableId={item.requestFileId} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <div className="px-8 py-0.5 flex justify-between items-center relative" key={item.fileId}>
                          <span className="text-base text-docsigna-blue-dark">{item.originalFilename}</span>
                          <div className="absolute left-0 top-[5px]">
                            <span className="block w-5 h-0.5 bg-gray-500 mt-1"></span>
                            <span className="block w-5 h-0.5 bg-gray-500 mt-1"></span>
                            <span className="block w-5 h-0.5 bg-gray-500 mt-1"></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 z-30 bg-docsigna-pink-light">
        {fileError ? (
          <div className="px-8 py-5 relative" style={{ color: colors.Red }}>
            <span>{fileError}</span>
            <IconButton
              size="small"
              onClick={() => setShowTooltip(!showTooltip)} // ← fixed typo
              sx={{ ml: 1, color: "grey" }}
            >
              <InfoIcon />
            </IconButton>

            {showTooltip && (
              <>
                <strong className="block mb-2">Please check your file</strong>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Your file may be corrupted.</li>
                  <li>Your file might be encrypted.</li>
                </ul>
              </>
            )}
          </div>
        ) : null}
        <div className="px-8 py-5 flex justify-end">
          <Button
            onClick={() => {
              clickedCloseForm = true;
              closeForm(true);
            }}
            disabled={mergingPending}
            className="pointer px-5 mr-3"
            text="Cancel"
            width="fit"
            variant="Secondary"
          />
          <div onClick={() => setErrorInitiated(true)}>
            <Button
              onClick={() => {
                if (validateForm()) saveFinalFiles();
              }}
              disabled={!validateForm() && errorInitiated}
              variant={mergingPending ? "loading" : "Primary"}
              className="pointer px-5"
              text="Save"
              width="fit"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FinalDocuments;
